import { definedOnly, mergeAnimationGroup, pickDefined, type WidgetDataBag } from '../patch-helpers';
import { WidgetPatchError } from '../widget-patch.error';
import { IMAGE_SOURCE_KEYS, IMAGE_STYLE_KEYS, type PartialImageWidgetData } from './partial-image-widget-data.schema';

const SOURCE_KEYS: ReadonlySet<string> = new Set(IMAGE_SOURCE_KEYS);
const STYLE_KEYS: ReadonlySet<string> = new Set(IMAGE_STYLE_KEYS);

/**
 * Thrown when a source field (`alt`, `link`, …) is patched onto a widget that has no image yet.
 * A distinct class so each caller can turn it into its own flavour of user-facing failure — without
 * it the merge produces a file-manager pick with no `url` and the failure surfaces as a raw zod
 * dump, when the caller's real problem is that there is nothing to annotate.
 *
 * **`WidgetPatchError`, not `Error`** — that base class is what `apps/mcp-backend` maps to a 400.
 * It extended plain `Error` until 2026-08-31, so a patch naming the one thing it should have named
 * came back as a 500: "the server broke", for the caller's own mistake. Its video twin
 * (`UnsupportedVideoUrlError`) got the base class the day the mapping was written; this one was
 * missed, and nothing failed because no test asked what STATUS it produced.
 */
export class MissingImageSourceError extends WidgetPatchError {
    constructor() {
        super('this image widget has no image yet — provide `url` to set one.');
        this.name = 'MissingImageSourceError';
    }
}

/**
 * Splits the flat AI patch back across the three levels of the real entity — `data`, `data.image`
 * (the file-manager pick) and the envelope's typed `styles` group. See
 * `partialImageWidgetDataSchema` for why the AI surface is flat in the first place, and
 * `text-widget-patch.ts` for why this fold is shared rather than re-declared per consumer.
 *
 * Two mappings are not a straight rename:
 *
 * - **borderRadius** writes all FOUR corner radii, not just `styles.borderRadius`. The view resolves
 *   each corner as `corner ?? borderRadius`, and the factory seeds every corner to `0` — a present
 *   `0` wins, so setting `borderRadius` alone would visibly do nothing. This mirrors
 *   `ImageControlComponent.setBorderRadius`, whose single radius input writes the four corners too.
 * - **animation** is a partial merged onto the current group — see `mergeAnimationGroup`.
 *
 * `storedFile` is the fourth level the flat surface deliberately cannot express: the ~40 fields of a
 * real file-manager pick (`path`, `name`, `extension`, `hasResize`, …). A model has no way to author
 * those, so they never appear in the schema — but without them the widget is only HALF a picked
 * image: it renders, and the editor's settings panel shows "choose a file" because
 * `SpFileManagerComponent.showCreateButton` is `!file?.path`. Whoever calls this fold with a file it
 * actually stored passes it here, and the flat fields are layered on top so an explicit `alt` still
 * wins over the file's own.
 */
export function foldImageWidgetPatch(
    currentData: WidgetDataBag,
    currentStyles: WidgetDataBag,
    patch: PartialImageWidgetData,
    storedFile?: WidgetDataBag | null
): { data: WidgetDataBag; styles: WidgetDataBag } {
    const { animation, borderRadius, ...flat } = patch;
    const currentImage = currentData['image'] as WidgetDataBag | null | undefined;

    const sourcePatch = pickDefined(flat as WidgetDataBag, IMAGE_SOURCE_KEYS);
    const stylePatch = pickDefined(flat as WidgetDataBag, IMAGE_STYLE_KEYS);
    const dataPatch = definedOnly(
        Object.fromEntries(
            Object.entries(flat as WidgetDataBag).filter(([key]) => !SOURCE_KEYS.has(key) && !STYLE_KEYS.has(key))
        )
    );

    const nextData: WidgetDataBag = { ...currentData, ...dataPatch };

    if (Object.keys(sourcePatch).length || storedFile) {
        if (!currentImage && !storedFile && sourcePatch['url'] === undefined) {
            throw new MissingImageSourceError();
        }

        // The stored file goes UNDER the flat patch and OVER whatever was there: it describes a
        // different file than the one the widget held, so keeping the old `path`/`name` beside the
        // new `url` would leave the panel pointing at a file the image no longer is.
        nextData['image'] = storedFile
            ? { ...storedFile, ...sourcePatch, url: storedFile['url'] }
            : { ...(currentImage ?? {}), ...sourcePatch };
    }

    if (animation) {
        nextData['animation'] = mergeAnimationGroup(currentData, animation as WidgetDataBag);
    }

    const nextStyles: WidgetDataBag = { ...currentStyles, ...stylePatch };

    if (borderRadius !== undefined) {
        nextStyles['borderRadius'] = borderRadius;
        nextStyles['borderTopLeftRadius'] = borderRadius;
        nextStyles['borderTopRightRadius'] = borderRadius;
        nextStyles['borderBottomRightRadius'] = borderRadius;
        nextStyles['borderBottomLeftRadius'] = borderRadius;
    }

    return { data: nextData, styles: nextStyles };
}
